export type CfAccessConfig = {
  jwksUrl: string;
  audience: string;
  issuer: string;
  teamDomain?: string;
};

export function getCfAccessConfig(): CfAccessConfig {
  const jwksUrl = import.meta.env.VITE_CF_ACCESS_JWKS_URL as string | undefined;
  const audience = import.meta.env.VITE_CF_ACCESS_AUD as string | undefined;
  const issuer = import.meta.env.VITE_CF_ACCESS_ISS as string | undefined;
  const teamDomain = import.meta.env.VITE_CF_TEAM_DOMAIN as string | undefined;
  const result: CfAccessConfig = {
    jwksUrl: jwksUrl || "",
    audience: audience || "",
    issuer: issuer || "",
  };
  if (teamDomain !== undefined) {
    result.teamDomain = teamDomain;
  }
  return result;
}

export function validateCfAccessConfig(): string[] {
  const cfg = getCfAccessConfig();
  const errs: string[] = [];
  if (!cfg.jwksUrl) errs.push("VITE_CF_ACCESS_JWKS_URL is not set");
  if (!cfg.audience) errs.push("VITE_CF_ACCESS_AUD is not set");
  if (!cfg.issuer) errs.push("VITE_CF_ACCESS_ISS is not set");
  if (cfg.jwksUrl && !cfg.jwksUrl.startsWith("https://")) errs.push("VITE_CF_ACCESS_JWKS_URL must be https");
  if (cfg.issuer && !cfg.issuer.startsWith("https://")) errs.push("VITE_CF_ACCESS_ISS must be https");
  return errs;
}

export function getCfAccessUrls() {
  const { issuer, teamDomain } = getCfAccessConfig();
  const base = issuer || (teamDomain ? `https://${teamDomain}` : "");
  return {
    login: (redirectUrl: string) => `${base}/cdn-cgi/access/login?redirect_url=${encodeURIComponent(redirectUrl)}`,
    logout: `${base}/cdn-cgi/access/logout`,
  };
}

export function getCsrfConfig() {
  const cookie = (import.meta.env.VITE_CSRF_COOKIE_NAME as string | undefined) || "CSRF-TOKEN";
  const header = (import.meta.env.VITE_CSRF_HEADER_NAME as string | undefined) || "X-CSRF-TOKEN";
  return { cookie, header };
}

export function getCsrfTokenFromCookie(): string | null {
  const { cookie } = getCsrfConfig();
  const match = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((c) => c.startsWith(`${cookie}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.split("=")[1] || "");
  } catch {
    return match.split("=")[1] || "";
  }
}

export function getRefreshUrl(): string {
  const { issuer, teamDomain } = getCfAccessConfig();
  const base = issuer || (teamDomain ? `https://${teamDomain}` : "");
  return `${base}/cdn-cgi/access/refresh`;
}

export function validateCookieAuth(): boolean {
  // Presence of CF_Authorization cookie is a good heuristic
  const name = (import.meta.env.VITE_CF_JWT_COOKIE_NAME as string | undefined) || "CF_Authorization";
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${name}=`));
}

export function getCfAccessJwtFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const name = (import.meta.env.VITE_CF_JWT_COOKIE_NAME as string | undefined) || "CF_Authorization";
  const match = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.split("=")[1] ?? "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
